import type { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import {
  Calendar,
  Home,
  House,
  Plus,
  Settings as SettingsIcon,
} from 'lucide-react-native';
import { ComponentType, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Fonts, Palette } from '@/constants/theme';
import { hapticTap } from '@/lib/haptics';
import { webTransition } from '@/lib/hover-style';

import { QuickActions } from './QuickActions';

interface TabConfig {
  name: string;
  title: string;
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
}

const TABS: TabConfig[] = [
  // "Today" replaces the v1 "Home" label so the busiest screen reads as
  // "what's on my plate today" (its actual contents — weather, tasks
  // due, calendar peek) instead of overloading the word "Home" with
  // the My Home tab next door.
  { name: 'index', title: 'Today', Icon: Home },
  { name: 'schedule', title: 'Schedule', Icon: Calendar },
  { name: 'my-home', title: 'My Home', Icon: House },
  { name: 'settings', title: 'Settings', Icon: SettingsIcon },
];

/**
 * Custom tab bar with a center floating "+" Quick Actions button. Two tabs
 * on each side of a circular FAB; FAB tap opens the Quick Actions sheet.
 *
 * Built for ADHD dopamine — the FAB has a subtle pulse and the press kicks
 * a satisfying scale. It's the fastest path to "do a thing" from anywhere.
 */
export function HomesteadTabBar({ state, navigation }: BottomTabBarProps) {
  const [quickOpen, setQuickOpen] = useState(false);
  const insets = useSafeAreaInsets();

  // Map state.routes to our tab config in order. Bottom-Tab Navigator gives us
  // the route names registered in (tabs)/_layout.tsx.
  const routes = state.routes.filter((r) => TABS.find((t) => t.name === r.name));

  const left = routes.slice(0, 2);
  const right = routes.slice(2, 4);

  return (
    <>
      <View
        style={[
          styles.bar,
          // Push the bar up by the system inset so Android 3-button nav /
          // gesture pill never overlaps the tab labels. iOS home indicator
          // gets the same treatment.
          { paddingBottom: Math.max(insets.bottom, 12) + 8 },
        ]}>
        <View style={styles.side}>
          {left.map((route) => {
            const conf = TABS.find((t) => t.name === route.name)!;
            const focused = state.routes[state.index].name === route.name;
            return (
              <TabButton
                key={route.key}
                conf={conf}
                focused={focused}
                onPress={() => navigation.navigate(route.name)}
              />
            );
          })}
        </View>

        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Quick actions"
          onPress={() => {
            hapticTap();
            setQuickOpen(true);
          }}
          style={({ pressed, hovered }) => [
            styles.fab,
            webTransition,
            hovered && styles.fabHover,
            pressed && styles.fabPressed,
          ]}>
          <View style={styles.fabHalo} />
          <Plus size={28} color={Palette.cream} strokeWidth={2.4} />
        </Pressable>

        <View style={styles.side}>
          {right.map((route) => {
            const conf = TABS.find((t) => t.name === route.name)!;
            const focused = state.routes[state.index].name === route.name;
            return (
              <TabButton
                key={route.key}
                conf={conf}
                focused={focused}
                onPress={() => navigation.navigate(route.name)}
              />
            );
          })}
        </View>
      </View>

      <QuickActions open={quickOpen} onClose={() => setQuickOpen(false)} />
    </>
  );
}

function TabButton({
  conf,
  focused,
  onPress,
}: {
  conf: TabConfig;
  focused: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="tab"
      accessibilityLabel={conf.title}
      accessibilityState={{ selected: focused }}
      onPress={onPress}
      style={({ pressed, hovered }) => [
        styles.tabBtn,
        webTransition,
        // Hover spice (Wave 67, user call): the old cream tint read as a
        // muddy brown with barely-there contrast. Unfocused tabs now
        // lift 1px and get a pale LAKE wash — the palette's one cool
        // tone — so hover states are unmistakably alive.
        hovered && !focused && styles.tabBtnHover,
        pressed && { opacity: 0.7 },
      ]}>
      {({ hovered }) => {
        const warm = hovered && !focused;
        const tint = focused ? Palette.forest : warm ? Palette.lakeDeep : Palette.inkFaint;
        return (
          <>
            <View
              style={[
                styles.tabIcon,
                webTransition,
                focused && styles.tabIconFocused,
                warm && styles.tabIconHover,
              ]}>
              <conf.Icon size={20} color={tint} strokeWidth={1.7} />
            </View>
            <Text style={[styles.tabLabel, { color: tint }]}>{conf.title}</Text>
          </>
        );
      }}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Palette.card,
    // Rounded top corners + a hairline on all four edges (top picks up
    // the rounding too) so the bar reads as a floating card capping the
    // bottom of the screen instead of a flat strip. The previous
    // flat-edge look gave it a square + utility feel that didn't match
    // the rest of the app's rounded surfaces.
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderTopColor: Palette.line,
    borderLeftColor: Palette.line,
    borderRightColor: Palette.line,
    paddingTop: 10,
    // paddingBottom is set inline so we can add the safe-area inset.
    paddingHorizontal: 10,
    minHeight: 88,
    // Subtle drop shadow lifts the bar off the page on light backgrounds
    // and reads as a distinct surface rather than a flat strip.
    shadowColor: '#000',
    shadowOpacity: 0.06,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: -3 },
    elevation: 8,
  },
  side: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  tabBtn: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 4,
    paddingHorizontal: 8,
    minWidth: 60,
    borderRadius: 14,
  },
  // Lake-tinted hover for unfocused tabs (Wave 67) — a cool wash with
  // real contrast against the cream bar, plus a 1px lift so the tab
  // physically responds. Focused tabs keep their mint pill instead.
  tabBtnHover: {
    backgroundColor: 'rgba(65,99,122,0.10)',
    transform: [{ translateY: -1 }],
  },
  tabIconHover: {
    backgroundColor: 'rgba(65,99,122,0.14)',
  },
  tabIcon: {
    width: 44,
    height: 32,
    // Full pill: borderRadius = half the height. At 12 the shape read as
    // a "slightly rounded square" on Android's native rendering — fine
    // on the web's anti-aliased corners, but visibly square on phone
    // pixel grids. 16 gives an unambiguous capsule on both platforms.
    // overflow:'hidden' makes sure the background paint respects the
    // rounded clip on Android even when transform:scale is applied
    // (a known RN quirk where the underlying view can paint past the
    // border-radius mask while animating).
    borderRadius: 16,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabIconFocused: {
    backgroundColor: '#DCE7E1',
    // Slight scale up on the active tab pill for unmistakable feedback.
    transform: [{ scale: 1.04 }],
    // Defensive duplicate of borderRadius — RN style merging order is
    // reliable but Android's view recycler occasionally drops the
    // unstyled-parent radius when a focused background is added on
    // re-render. Setting it here too is cheap insurance.
    borderRadius: 16,
  },
  tabLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 12.5,
    marginTop: 3,
    letterSpacing: 0.3,
  },
  fab: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: Palette.forest,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 8,
    marginTop: -28, // float above the bar
    shadowColor: Palette.forest,
    shadowOpacity: 0.4,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 8 },
    elevation: 8,
  },
  fabPressed: {
    transform: [{ scale: 0.94 }],
  },
  // Gentle lift on hover — the FAB is the most-tapped UI element, so
  // a small "ready to be pressed" cue rewards mouse users without
  // shouting. 4% scale + a hint of brighter shadow keeps it calm.
  fabHover: {
    transform: [{ scale: 1.04 }],
    shadowOpacity: 0.5,
  },
  // Wave 61: the honey OUTLINE ring read as an uncommitted tan border
  // (user flagged it). Replaced with a filled cream plate behind the
  // FAB — the classic docked-FAB "cut out of the bar" illusion. The
  // green circle now sits in a soft notch instead of wearing a ring.
  fabHalo: {
    position: 'absolute',
    width: 68,
    height: 68,
    borderRadius: 34,
    borderWidth: 4,
    borderColor: Palette.cream,
  },
});
